/**
 * The encounter engine — one fight, at three scales.
 *
 * This file is `boss.ts` with the kingdom taken out of it. A boss battle was
 * always a general shape wearing a dragon: staged questions drawn from the
 * ordinary exercise bank, scored per stage, derived rather than stored, free to
 * lose and free to walk away from. `docs/rpg.md` §15 asked for that shape at
 * kingdom scale and the platform built it there first, which is why it lived in
 * a file named after the boss.
 *
 * The adventure needs the same shape one and two scales down — a region's
 * haunt, and the concept-scale encounter that is the whole point of a warded
 * site — and the wrong way to get it is a second reducer. Two state machines
 * over the same battle would drift on exactly the questions that are hard to
 * get right and easy to get subtly wrong: what a malformed answer does, whether
 * a failed stage ends the attempt, whether a retreat is a loss. So there is one
 * engine, here, and `boss.ts` is a thin specialisation of it whose public API
 * has not changed by a character.
 *
 * Everything `boss.ts` promised, this file now owes:
 *
 * **The fight is derived, not stored.** Same inputs, same battle — see the
 * determinism note on `startEncounter`. A reloaded page, a restored background
 * tab and a React re-render all rebuild the identical fight from
 * (learner, def, attempt) plus the flat list of answers the profile kept.
 *
 * **Malformed input is not a wrong answer** (`CLAUDE.md`). An empty box, "half"
 * or `3/0` moves nothing: not the score, not the question index, not the band,
 * not the review list.
 *
 * **Losing costs nothing** (`docs/rpg.md` §12). No HP for the student, no gold
 * penalty, no lost streak, nothing written to the profile. A defeat produces
 * exactly one artefact — the concepts the wrong answers touched — and the retry
 * is the next `attemptIndex`, with fresh problems.
 *
 * **The mathematics still counts.** Every graded answer must *also* go through
 * `applyAttempt`; the integration note at the bottom of `boss.ts` is the
 * contract and it applies to every encounter, not only to bosses.
 *
 * What is new here is the one thing a concept-scale fight needs and a boss does
 * not: **the band may move with the student** (`docs/rpg.md` §16). See
 * `adaptiveBand`, and see `drawPending` for how that stays deterministic.
 */

import {
  fingerprint,
  type Difficulty,
  type Exercise,
  type ExerciseBank,
  type GradeResult,
} from '@yuan-math/exercises';
import type { BossAnswer } from '@yuan-math/learning';

/* -------------------------------------------------------------------------- */
/* The definition                                                             */
/* -------------------------------------------------------------------------- */

/**
 * How a stage chooses the difficulty of each question.
 *
 * `fixed` is the boss's rule: every question in the stage is asked at the same
 * band, and the bands climb between stages. A ceremony should be the same
 * mountain for everyone who walks up to it, and a dragon that quietly got
 * easier because you were struggling would make the victory worth less than the
 * student thinks it is.
 *
 * `adaptive` is the rule for everything smaller. A warded site is practice with
 * a story on it, and §16 asks practice to move with the learner — up when they
 * are fluent, down and scaffolded when they are not, so that nobody is ever
 * permanently stuck. See `adaptiveBand`.
 */
export type Banding = 'fixed' | 'adaptive';

/**
 * One phase of the fight: a named slice of mathematics.
 *
 * `conceptIds` are dealt round-robin across the stage's questions, so a stage
 * of four questions over two concepts asks two of each — a stage is a *theme*,
 * not a single skill, and "Scales of Ten" that turned out to be four rounding
 * questions would be a difficulty band wearing a name.
 */
export type EncounterStage = {
  /** Fantasy name, shown as the stage banner. "The Prime Flame". */
  name: string;
  /** Concepts with at least one template each. */
  conceptIds: string[];
  /**
   * Under `fixed`, the band every question in this stage is asked at, and it
   * never falls between stages. Under `adaptive`, the band the stage *opens*
   * at — where it goes from there is the student's business.
   */
  difficulty: Difficulty;
  /** 3–5. Below three, one slip is 33% and the threshold stops meaning anything. */
  questionCount: number;
  /** Defaults to `fixed`, which is what every authored boss stage is. */
  banding?: Banding;
};

export type EncounterDef = {
  id: string;
  /** One to three. §15's dragon has three; a warded site has one. */
  stages: EncounterStage[];
  /**
   * The share of a stage's questions that must be right for it to pass.
   * Defaults to `STAGE_PASS_SHARE`. It is per-definition rather than global so
   * a skirmish may be gentler than a dragon without the dragon being softened.
   */
  passShare?: number;
};

/**
 * Everything needed to draw a question: who is fighting, what, and on which
 * retry.
 *
 * Bundled into one object because the adaptive path has to draw questions
 * *during* the fight rather than only at the start, and threading five loose
 * parameters through the reducer would guarantee that two call sites eventually
 * disagree about the seed — which is the one thing determinism cannot survive.
 *
 * `bank` is a parameter for the reason the quest catalogue is: `packages/world`
 * sits below `content/`, so it cannot import the bank the app actually uses.
 */
export type EncounterRun = {
  def: EncounterDef;
  bank: ExerciseBank;
  learnerId: string;
  /** Which retry this is, from zero. The seed depends on it; nothing else does. */
  attemptIndex: number;
};

/* -------------------------------------------------------------------------- */
/* The band                                                                   */
/* -------------------------------------------------------------------------- */

/** The bank's difficulty range: 1 = first exposure, 5 = challenge. */
export const BAND_MIN: Difficulty = 1;
export const BAND_MAX: Difficulty = 5;

/**
 * Consecutive correct answers before the band steps up. One wrong answer steps
 * it back down immediately.
 *
 * The asymmetry is deliberate and is the whole of §16's tone: it should be
 * easier to be given help than to be given a harder problem. Stepping up on a
 * single correct answer would let one lucky guess hand a struggling student the
 * challenge band, and stepping down only after two misses would make them sit
 * through a second failure to earn the scaffolding they already needed.
 */
export const BAND_STEP_UP_AFTER = 2;

/**
 * Where an adaptive stage stands after a run of answers. Pure, and a fold —
 * which is what keeps an adaptive fight as replayable as a fixed one.
 *
 * `history` is correct/wrong in order, and holds *only* graded answers: a
 * malformed response never reaches it, so fumbling the keyboard cannot make the
 * next question easier.
 *
 * The band is re-derived from the whole history on every read rather than
 * carried forward and mutated. Storing "the band right now" would be a second
 * account of the fight that a replay could contradict, and the fight already
 * has one account: the answers.
 */
export function adaptiveBand(opening: Difficulty, history: readonly boolean[]): Difficulty {
  let band = opening;
  let run = 0;

  for (const correct of history) {
    if (!correct) {
      band = step(band, -1);
      run = 0;
      continue;
    }
    run += 1;
    if (run >= BAND_STEP_UP_AFTER) {
      band = step(band, +1);
      run = 0;
    }
  }

  return band;
}

function step(band: Difficulty, by: 1 | -1): Difficulty {
  return Math.min(BAND_MAX, Math.max(BAND_MIN, band + by)) as Difficulty;
}

/**
 * The band an encounter should open at, from what the platform already believes
 * about the student: the concept's overall mastery, 0–1.
 *
 * The thresholds are `masteryLevel`'s, deliberately — the platform should not
 * hold two opinions about what 0.65 means, one of them called "familiar" on the
 * progress page and the other called "band 3" in a fight.
 *
 * The ceiling is 4 rather than 5. Band 5 is the challenge rung, and an
 * encounter that *opened* there would be asking a student to prove themselves
 * before it had asked them anything; the ladder can climb to it in two correct
 * answers, which is the honest way to arrive.
 */
export function openingBand(mastery: number): Difficulty {
  if (mastery < 0.4) return 1;
  if (mastery < 0.7) return 2;
  if (mastery < 0.85) return 3;
  return 4;
}

/* -------------------------------------------------------------------------- */
/* The fight                                                                  */
/* -------------------------------------------------------------------------- */

/** The default share of a stage's questions that must be right to pass it. */
export const STAGE_PASS_SHARE = 0.75;

/**
 * How many times a duplicate question is re-seeded before it is accepted.
 *
 * A template with a narrow parameter space can hand back the same problem twice
 * at the same difficulty, and a four-question stage that asks `7 × 8` twice is
 * a stage a student can pass by knowing half of it. Bounded, and deterministic:
 * the retry suffix is derived from the attempt number, never rolled.
 */
const DUPLICATE_RETRIES = 4;

export type EncounterQuestion = {
  stageIndex: number;
  /** Position within the stage, from zero. */
  index: number;
  conceptId: string;
  /**
   * The band it was *asked* at, which is not always `exercise.difficulty`:
   * `bank.single` falls back to the nearest difficulty the chosen template
   * actually supports. The ladder has to climb from what was asked for, or a
   * concept whose templates stop at band 3 would peg an adaptive stage there
   * for ever.
   */
  difficulty: Difficulty;
  exercise: Exercise;
};

export type EncounterStageState = {
  name: string;
  /** Fixed: the band of every question. Adaptive: the band it opened at. */
  difficulty: Difficulty;
  banding: Banding;
  /**
   * How many questions this stage plans to ask. Read this for a progress
   * indicator rather than `questions.length`: under `adaptive` the questions
   * are drawn as the fight reaches them, so the array is short until the end.
   */
  questionCount: number;
  /** Drawn so far. Complete from the start under `fixed`. */
  questions: EncounterQuestion[];
  correct: number;
  wrong: number;
  /** Graded answers in order. The adaptive ladder's only input. */
  history: boolean[];
  /** Undefined until the stage's questions are exhausted. */
  passed?: boolean;
};

/**
 * Where the fight stands.
 *
 * `stage-cleared` is a pause rather than a state of the mathematics: the stage
 * is passed and the next one has not started, so a page can show the boss
 * changing form before the student is asked anything else. `retreated` is
 * walking away, which is free and is not a loss.
 */
export type EncounterOutcome = 'fighting' | 'stage-cleared' | 'won' | 'lost' | 'retreated';

export type EncounterState = {
  defId: string;
  learnerId: string;
  attemptIndex: number;
  startedAt: number;
  stages: EncounterStageState[];
  stageIndex: number;
  questionIndex: number;
  outcome: EncounterOutcome;
  /**
   * Wrong answers per concept, which is the whole of what a defeat produces.
   * Read it through `conceptsToReview`.
   */
  missed: Record<string, number>;
};

export type EncounterEvent =
  | { kind: 'answer'; gradeResult: GradeResult }
  | { kind: 'advance' }
  | { kind: 'retreat' };

/**
 * Builds the fight.
 *
 * **The invariant that makes this design work: same inputs, same battle.**
 * Every question is seeded from `(learnerId, def.id, stageIndex, questionIndex,
 * attemptIndex)` and, under `adaptive`, the band — which is itself a fold over
 * the answers so far. Nothing anywhere reads a clock or a random number, so
 * calling this twice with the same arguments produces byte-identical exercises,
 * and replaying the same answers walks through byte-identical questions.
 *
 * That is what lets a fight in progress be *re-derived* rather than stored: a
 * student who reloads mid-fight is not quietly handed a different question than
 * the one they were halfway through. Storing generated problems in the profile
 * would be the alternative, and it would put a copy of the exercise bank in
 * every learner's `localStorage`.
 *
 * A retry is `attemptIndex + 1` and therefore a genuinely different fight —
 * beating the Number Dragon by memorising four answers is not beating it.
 *
 * `now` is injected, like everywhere below `apps/web`, and is recorded rather
 * than used: the seed deliberately does *not* depend on it, because a seed that
 * moved with the clock is exactly the reload bug this function exists to avoid.
 *
 * Fixed stages are drawn whole, here. Adaptive stages are drawn a question at a
 * time, because the band of question *n* is not knowable until question *n − 1*
 * has been answered — only the first question of the opening stage can be known
 * up front, and `drawPending` draws the rest.
 */
export function startEncounter(run: EncounterRun, now: number): EncounterState {
  const stages = run.def.stages.map((stage, stageIndex) => {
    const banding = stage.banding ?? 'fixed';
    const base: EncounterStageState = {
      name: stage.name,
      difficulty: stage.difficulty,
      banding,
      questionCount: stage.questionCount,
      questions: [],
      correct: 0,
      wrong: 0,
      history: [],
    };

    if (banding === 'adaptive') return base;

    const questions: EncounterQuestion[] = [];
    for (let index = 0; index < stage.questionCount; index += 1) {
      questions.push(draw(run, stage, stageIndex, index, stage.difficulty, questions));
    }
    return { ...base, questions };
  });

  const state: EncounterState = {
    defId: run.def.id,
    learnerId: run.learnerId,
    attemptIndex: run.attemptIndex,
    startedAt: now,
    stages,
    stageIndex: 0,
    questionIndex: 0,
    outcome: 'fighting',
    missed: {},
  };

  return drawPending(run, state);
}

/**
 * Draws one question, avoiding a problem the stage has already asked.
 *
 * `bank.single` is the established path from "a concept at a target difficulty"
 * to a concrete problem: it picks among the concept's templates by
 * `hashString(seed)`, falls back to the nearest difficulty the chosen template
 * actually supports, and calls `generateExercise`, which seeds `createRng` from
 * the same string. One hashing scheme, in `@yuan-math/math`, shared with the
 * practice page, the diagnostic and every fight in the world.
 */
function draw(
  run: EncounterRun,
  stage: EncounterStage,
  stageIndex: number,
  index: number,
  band: Difficulty,
  drawn: readonly EncounterQuestion[],
): EncounterQuestion {
  // Round-robin, so the stage covers its theme rather than its first concept.
  const conceptId = stage.conceptIds[index % stage.conceptIds.length] as string;
  const banding = stage.banding ?? 'fixed';
  const base = questionSeed(run, stageIndex, index, banding === 'adaptive' ? band : undefined);
  const seen = new Set(drawn.map((question) => fingerprint(question.exercise)));

  let exercise: Exercise | undefined;
  for (let retry = 0; retry <= DUPLICATE_RETRIES; retry += 1) {
    const candidate = run.bank.single(conceptId, band, retry === 0 ? base : `${base}:r${retry}`);
    if (!candidate) break;
    exercise = candidate;
    if (!seen.has(fingerprint(candidate))) break;
  }

  if (!exercise) {
    // `validateBosses` rules this out in content; a stage that silently ran
    // three questions short would be a fight nobody could read the score of.
    throw new Error(`Encounter "${run.def.id}" stage ${stageIndex} has no exercise for concept "${conceptId}"`);
  }

  return { stageIndex, index, conceptId, difficulty: band, exercise };
}

/**
 * The seed for one question. The single place the engine's determinism lives.
 *
 * The band is appended only for an adaptive stage, and that omission is
 * load-bearing rather than tidy: it is what keeps every authored boss drawing
 * the exact problems it drew before this file existed.
 */
function questionSeed(
  run: EncounterRun,
  stageIndex: number,
  questionIndex: number,
  band?: Difficulty,
): string {
  const base = `${run.learnerId}:${run.def.id}:${stageIndex}:${questionIndex}:${run.attemptIndex}`;
  return band === undefined ? base : `${base}:b${band}`;
}

/**
 * Where the fight is standing without a question in front of it, if anywhere.
 *
 * Only ever an adaptive stage: a fixed one was drawn whole at the start.
 */
function pending(state: EncounterState): { stageIndex: number; questionIndex: number } | undefined {
  if (state.outcome !== 'fighting') return undefined;
  const stage = state.stages[state.stageIndex];
  if (!stage || stage.banding !== 'adaptive') return undefined;
  if (state.questionIndex >= stage.questionCount) return undefined;
  if (stage.questions[state.questionIndex]) return undefined;
  return { stageIndex: state.stageIndex, questionIndex: state.questionIndex };
}

/**
 * Draws the question the fight is waiting on, if it is waiting on one.
 *
 * This is the lazy half of the adaptive path, and the reason the reducer below
 * can stay a pure function over plain data with no exercise bank in it. The
 * band comes from `adaptiveBand` folded over the stage's own answers, so this
 * is still a pure function of (run, state) — call it twice and get the same
 * question, replay the same answers and get the same sequence.
 *
 * Identity when nothing is pending, which is always the case for a boss.
 */
export function drawPending(run: EncounterRun, state: EncounterState): EncounterState {
  const at = pending(state);
  if (!at) return state;

  const definition = run.def.stages[at.stageIndex];
  const stage = state.stages[at.stageIndex];
  if (!definition || !stage) return state;

  const band = adaptiveBand(definition.difficulty, stage.history);
  const question = draw(run, definition, at.stageIndex, at.questionIndex, band, stage.questions);

  return {
    ...state,
    stages: replace(state.stages, at.stageIndex, {
      ...stage,
      questions: [...stage.questions, question],
    }),
  };
}

/**
 * The battle state machine. Pure, no clock, one state in and one state out.
 *
 * An event that does not apply returns the state *identically* — the same
 * object, so a React caller re-rendering on every keystroke cannot be made to
 * re-render by a no-op.
 *
 * This does not draw questions, which is why it needs no bank and why an
 * adaptive fight must be driven through `stepEncounter` instead. A boss may
 * call it directly: every one of its stages was drawn whole at the start.
 */
export function encounterReducer(state: EncounterState, event: EncounterEvent, passShare = STAGE_PASS_SHARE): EncounterState {
  switch (event.kind) {
    case 'answer':
      return answer(state, event.gradeResult, passShare);

    case 'advance':
      // Only a cleared stage advances. Nothing skips a stage, and nothing
      // restarts a finished fight — starting the next attempt does.
      if (state.outcome !== 'stage-cleared') return state;
      return { ...state, stageIndex: state.stageIndex + 1, questionIndex: 0, outcome: 'fighting' };

    case 'retreat':
      // Free, and not a loss. Walking away from a dragon you are not ready for
      // is the correct move, and §12 says the system must not punish it.
      if (state.outcome === 'won' || state.outcome === 'lost' || state.outcome === 'retreated') return state;
      return { ...state, outcome: 'retreated' };
  }
}

/**
 * One turn of an encounter: score the event, then draw whatever the fight is
 * now waiting on.
 *
 * The general entry point, and the only correct one for an adaptive fight.
 * Identical to `encounterReducer` for a fixed one, which is why `boss.ts` is
 * free to keep exporting the bare reducer.
 */
export function stepEncounter(run: EncounterRun, state: EncounterState, event: EncounterEvent): EncounterState {
  return drawPending(run, encounterReducer(state, event, run.def.passShare ?? STAGE_PASS_SHARE));
}

function answer(state: EncounterState, result: GradeResult, passShare: number): EncounterState {
  /*
   * Malformed input is not an answer, so it is not a wrong answer either
   * (`CLAUDE.md`). Nothing moves: not the score, not the question, not the
   * band, not the review list. The student simply gets the same question back.
   */
  if (result.status === 'malformed') return state;
  if (state.outcome !== 'fighting') return state;

  const stage = state.stages[state.stageIndex];
  const question = stage?.questions[state.questionIndex];
  if (!stage || !question) return state;

  const correct = result.correct;
  const scored: EncounterStageState = {
    ...stage,
    correct: stage.correct + (correct ? 1 : 0),
    wrong: stage.wrong + (correct ? 0 : 1),
    history: [...stage.history, correct],
  };

  const missed = correct
    ? state.missed
    : { ...state.missed, [question.conceptId]: (state.missed[question.conceptId] ?? 0) + 1 };

  const answered = state.questionIndex + 1;
  const exhausted = answered >= stage.questionCount;

  if (!exhausted) {
    return {
      ...state,
      stages: replace(state.stages, state.stageIndex, scored),
      questionIndex: answered,
      missed,
    };
  }

  // The stage is over, so it is scored. Rounded up: three of four is 0.75 and
  // passes; two of three is 0.667 and does not.
  const passed = scored.correct >= Math.ceil(passShare * scored.questionCount);
  const settled: EncounterStageState = { ...scored, passed };
  const lastStage = state.stageIndex >= state.stages.length - 1;

  return {
    ...state,
    stages: replace(state.stages, state.stageIndex, settled),
    questionIndex: answered,
    missed,
    /*
     * A failed stage ends the attempt. The stages climb in difficulty, so
     * marching a student who could not pass "Scales of Ten" through the prime
     * flame teaches them nothing and reads as the game rubbing it in — and the
     * defeat screen already has what it needs, because `missed` names exactly
     * the concepts that went wrong.
     */
    outcome: !passed ? 'lost' : lastStage ? 'won' : 'stage-cleared',
  };
}

function replace(
  stages: EncounterStageState[],
  index: number,
  stage: EncounterStageState,
): EncounterStageState[] {
  return stages.map((entry, i) => (i === index ? stage : entry));
}

/**
 * Rebuilds a fight from the answers the profile kept.
 *
 * This is the other half of the determinism note above, and the reason a fight
 * can be persisted in two numbers. `startEncounter` re-derives the questions
 * from (learner, def, attempt); replaying the stored correct/wrong list against
 * them re-derives everything else — the score, the stage, the band, the
 * question on screen, the concepts to go back to. A page that has been
 * reloaded, restored from a background tab or opened on the student's other
 * device therefore resumes on the exact question it left, with the exact dots
 * above it.
 *
 * Stage boundaries are the definition's, never the student's, so a stage that
 * ended is advanced past here rather than being recorded as having ended.
 * A *trailing* cleared stage is left standing: that pause is the moment the
 * boss changes form, and it is the caller's to show.
 *
 * The grade results are reconstructed as the minimum the reducer reads — a
 * status and a verdict. Nothing else about the original grading survives, and
 * nothing else needs to: the wrong answer's diagnosis went to `applyAttempt`
 * and lives in the attempt log, where the platform's account of what a student
 * can do belongs.
 */
export function replayEncounter(
  run: EncounterRun,
  answers: readonly BossAnswer[],
  now: number,
): EncounterState {
  let state = startEncounter(run, now);
  for (const result of answers) {
    if (state.outcome === 'stage-cleared') state = stepEncounter(run, state, { kind: 'advance' });
    state = stepEncounter(run, state, {
      kind: 'answer',
      gradeResult: result === 'correct' ? REPLAY_CORRECT : REPLAY_WRONG,
    });
  }
  return state;
}

const REPLAY_CORRECT: GradeResult = { status: 'correct', correct: true, expected: '', feedback: '' };
const REPLAY_WRONG: GradeResult = { status: 'incorrect', correct: false, expected: '', feedback: '' };

/* -------------------------------------------------------------------------- */
/* Reading a fight                                                            */
/* -------------------------------------------------------------------------- */

/** The question on screen, or `undefined` when the fight is not asking one. */
export function currentQuestion(state: EncounterState): EncounterQuestion | undefined {
  if (state.outcome !== 'fighting') return undefined;
  return state.stages[state.stageIndex]?.questions[state.questionIndex];
}

/** How many questions the whole fight plans to ask. */
export function encounterQuestionCount(def: EncounterDef): number {
  return def.stages.reduce((sum, stage) => sum + stage.questionCount, 0);
}

/** Every concept the encounter tests, once each, in stage order. */
export function encounterConceptIds(def: EncounterDef): string[] {
  return [...new Set(def.stages.flatMap((stage) => stage.conceptIds))];
}

/**
 * How ready a student is for the fight — as a count and a list, never as a
 * verdict.
 *
 * There is deliberately no `ready: boolean` here and no threshold anywhere: a
 * gate states what it will ask and how much of it the student can already do,
 * and then opens (docs/rpg-implementation-plan.md §0.1). Somebody who wants to
 * go and meet the dragon at three skills out of nine is allowed to, and finding
 * the edge of what you know is worth something (§12).
 *
 * `known` is the student's `knownSet`, not their mastered set: the same
 * distinction the rest of this package draws — mastery is a claim earned by
 * practice, while "can they do this yet?" is asked with `knownSet`.
 */
export function encounterReadiness(
  def: EncounterDef,
  known: ReadonlySet<string>,
): { known: number; total: number; unknownConceptIds: string[] } {
  const conceptIds = encounterConceptIds(def);
  const unknownConceptIds = conceptIds.filter((id) => !known.has(id));
  return { known: conceptIds.length - unknownConceptIds.length, total: conceptIds.length, unknownConceptIds };
}

/**
 * What a defeat should send the student back to: the concepts their wrong
 * answers touched, worst first.
 *
 * This is the one thing a lost fight produces, and it is the reason the loss is
 * allowed to exist at all (§12: "You discovered a weakness"). It is derived
 * from the fight rather than written to the profile, because the profile
 * already has the honest version — every one of those answers went through
 * `applyAttempt` and moved real mastery.
 */
export function conceptsToReview(state: EncounterState): string[] {
  return Object.entries(state.missed)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([conceptId]) => conceptId);
}
